/**
 * SRES · GIS Studio panel (Sprint P17 · Feb 2026)
 * ═══════════════════════════════════════════════
 *
 * End-to-end workflow embedded inside the Data Validation wizard step:
 *
 *   1. Upload boundary  → 2. Auto-detected location  → 3. Approve  →
 *   4. Select preset + buffer  → 5. Generate  → 6. Layer tree · ZIP · CPR
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  UploadSimple, CheckCircle, MapPin, Circle, Play,
  Warning, DownloadSimple, FolderSimple, ArrowClockwise,
  FileArrowDown, Certificate, Trash, Compass,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { WizardCard, StatusRow, KV } from "@/pages/wizard/_shared";


const QUALITY_COLOR = {
  High:                   "bg-emerald-100 text-emerald-700 border-emerald-300",
  Medium:                 "bg-amber-100  text-amber-700  border-amber-300",
  Low:                    "bg-rose-100   text-rose-700   border-rose-300",
  Unavailable:            "bg-slate-100  text-slate-500  border-slate-300",
  "Source Unavailable":   "bg-slate-100  text-slate-500  border-slate-300",
};

const STAGE_LABEL = {
  boundary_validation:    "Boundary Validation",
  location_identification:"Location Identification",
  crs_transformation:     "CRS Transformation",
  boundary:               "Project Boundary",
  buffered_boundary:      "Buffered Boundary",
  basemap:                "Satellite Basemap",
  dem:                    "DEM (SRTM 30 m)",
  hillshade:              "Hillshade",
  slope:                  "Slope Map",
  aspect:                 "Aspect Map",
  contours:               "Contours",
  roads:                  "OSM Roads",
  drainage:               "OSM Waterways",
  settlements:            "OSM Settlements",
  satellite_true:         "Sentinel-2 True Colour",
  satellite_false:        "Sentinel-2 False Colour",
  regional_geology:       "Regional Geology",
  lineaments:             "AI Lineaments",
  mineral_occurrences:    "Mineral Occurrences (USGS MRDS)",
  land_cover:             "ESA WorldCover",
  forest:                 "Forest Cover (Hansen)",
  water_bodies:           "Water Bodies",
  protected_areas:        "Protected Areas (WDPA)",
  sensitive_receptors:    "Sensitive Receptors",
  terrain_analysis:       "Terrain Ruggedness",
  watersheds:             "Watersheds",
  save_library:           "Save to Library",
  pipeline:               "Pipeline Complete",
};


export default function GISStudioPanel({ projectId }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("quick");
  const [bufferM, setBufferM] = useState(2000);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const fileInputRef = useRef(null);

  const boundaryQ = useQuery({
    queryKey: ["gis-boundary", projectId],
    queryFn: () => api.get(`/projects/${projectId}/gis/boundary`)
                     .then((r) => r.data)
                     .catch(() => null),
    enabled: !!projectId, retry: false, staleTime: 0,
  });
  const presetsQ = useQuery({
    queryKey: ["gis-presets"],
    queryFn: () => api.get(`/gis/presets`).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const libraryQ = useQuery({
    queryKey: ["gis-library", projectId],
    queryFn: () => api.get(`/projects/${projectId}/gis/library`)
                     .then((r) => r.data)
                     .catch(() => ({ count: 0, layers: [] })),
    enabled: !!projectId,
  });
  const statusQ = useQuery({
    queryKey: ["gis-status", projectId],
    queryFn: () => api.get(`/projects/${projectId}/gis/status`).then((r) => r.data),
    enabled: !!projectId && pipelineRunning,
    refetchInterval: pipelineRunning ? 3000 : false,
  });
  useEffect(() => {
    if (statusQ.data?.status === "complete" || statusQ.data?.status === "failed") {
      setPipelineRunning(false);
      qc.invalidateQueries({ queryKey: ["gis-library", projectId] });
      if (statusQ.data.status === "complete") {
        toast.success(`GIS pipeline complete · ${statusQ.data.layers} layers`);
      } else {
        toast.error("GIS pipeline failed — check the log");
      }
    }
  }, [statusQ.data?.status, statusQ.data?.layers, projectId, qc]);

  // Sync bufferM to preset default whenever the mode changes.
  useEffect(() => {
    const p = presetsQ.data?.presets?.find((x) => x.code === mode);
    if (p && !boundaryQ.data?.approved) setBufferM(p.default_buffer_m);
  }, [mode, presetsQ.data, boundaryQ.data?.approved]);

  const boundary  = boundaryQ.data;
  const uploaded  = !!boundary;
  const approved  = !!boundary?.approved;
  const location  = boundary?.location || {};

  const uploadMut = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      return api.post(`/projects/${projectId}/gis/boundary/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success("Boundary uploaded & location identified");
      qc.invalidateQueries({ queryKey: ["gis-boundary", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
    onSettled: () => setUploadBusy(false),
  });
  const approveMut = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/gis/boundary/approve`, {})
                        .then((r) => r.data),
    onSuccess: () => {
      toast.success("Boundary approved");
      qc.invalidateQueries({ queryKey: ["gis-boundary", projectId] });
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/projects/${projectId}/gis/boundary`)
                        .then((r) => r.data),
    onSuccess: () => {
      toast.success("Boundary + GIS library cleared");
      qc.invalidateQueries({ queryKey: ["gis-boundary", projectId] });
      qc.invalidateQueries({ queryKey: ["gis-library", projectId] });
    },
  });
  const generateMut = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/gis/generate`,
        { mode, buffer_m: bufferM }).then((r) => r.data),
    onSuccess: () => {
      setPipelineRunning(true);
      toast.success("GIS pipeline queued");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setUploadBusy(true); uploadMut.mutate(f);
  };

  const downloadZip = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/gis/package.zip`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "gis_package.zip"; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const layerTree = useMemo(() => {
    const byCat = {};
    (libraryQ.data?.layers || []).forEach((l) => {
      const cat = l.layerCategory || "Uncategorized";
      (byCat[cat] ||= []).push(l);
    });
    return Object.entries(byCat).sort(([a], [b]) => a.localeCompare(b));
  }, [libraryQ.data]);

  return (
    <div className="space-y-4" data-testid="gis-studio-panel">
      {/* ── 1. Upload / status ────────────────────────────────────── */}
      <WizardCard title="Automatic GIS Data Generation"
                    subtitle="Upload a project boundary and let SRES auto-fetch every available free GIS layer"
                    testId="gis-studio-header">
        <StatusRow tone={approved ? "ok" : uploaded ? "info" : "warn"}>
          {!uploaded && (
            <>No boundary uploaded yet. Supported: KML · KMZ · GeoJSON.</>
          )}
          {uploaded && !approved && (
            <>Boundary detected — review the identified location below and click <b>Approve</b>.</>
          )}
          {approved && (
            <><b>Approved.</b> {location.country && `${location.country} · ${location.state || ""} · ${location.district || ""}`}</>
          )}
        </StatusRow>

        <div className="flex flex-wrap gap-2 items-center pt-2">
          <input
            ref={fileInputRef}
            type="file" accept=".kml,.kmz,.geojson,.json"
            onChange={onFile} className="hidden"
            data-testid="gis-boundary-file-input"
          />
          <Button size="sm" variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadBusy}
                  data-testid="gis-boundary-upload-btn">
            <UploadSimple size={14} className="mr-1.5" />
            {uploaded ? "Replace boundary" : "Upload boundary"}
          </Button>
          {uploaded && !approved && (
            <Button size="sm" onClick={() => approveMut.mutate()}
                     className="bg-emerald-600 hover:bg-emerald-700 text-white"
                     data-testid="gis-boundary-approve-btn">
              <CheckCircle size={14} className="mr-1.5" weight="fill" />
              Approve boundary
            </Button>
          )}
          {uploaded && (
            <Button size="sm" variant="ghost"
                    onClick={() => { if (confirm("Delete boundary + generated GIS library?")) deleteMut.mutate(); }}
                    className="text-rose-600 hover:text-rose-700"
                    data-testid="gis-boundary-delete-btn">
              <Trash size={14} className="mr-1.5" />
              Clear
            </Button>
          )}
        </div>
      </WizardCard>

      {/* ── 2. Identified location ──────────────────────────────── */}
      {uploaded && (
        <WizardCard title="Identified location" testId="gis-location-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KV k="Country"        v={location.country || "—"} />
            <KV k="State/Region"   v={location.state   || "—"} />
            <KV k="District"       v={location.district || "—"} />
            <KV k="Nearest town"   v={location.nearest_settlement || "—"} />
            <KV k="Latitude"       v={boundary.centroid[1].toFixed(4)} />
            <KV k="Longitude"      v={boundary.centroid[0].toFixed(4)} />
            <KV k="UTM zone"       v={`EPSG:${boundary.utm_epsg} (${boundary.hemisphere})`} />
            <KV k="Approx. elevation" v={location.approx_elevation_m
                                            ? `${Math.round(location.approx_elevation_m)} m` : "—"} />
            <KV k="Area"           v={`${boundary.area_km2} km²`} />
            <KV k="Perimeter"      v={`${boundary.perimeter_km} km`} />
          </div>
        </WizardCard>
      )}

      {/* ── 3. Preset + buffer + generate ──────────────────────── */}
      {approved && (
        <WizardCard title="Generate GIS Data"
                     subtitle="Pick a package and buffer; SRES will fetch layers from free open sources"
                     testId="gis-generate-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">
                Preset package
              </Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="gis-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(presetsQ.data?.presets || []).map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.label} · {p.layers.length} layers
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">
                Buffer distance (m)
              </Label>
              <Select value={String(bufferM)}
                       onValueChange={(v) => setBufferM(Number(v))}>
                <SelectTrigger data-testid="gis-buffer-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1000, 2000, 5000, 10000, 25000, 50000].map((b) => (
                    <SelectItem key={b} value={String(b)}>
                      {b === 0 ? "Boundary only" : `+${(b / 1000).toFixed(0)} km buffer`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={() => generateMut.mutate()}
                       disabled={pipelineRunning || !approved}
                       className="w-full bg-brand-blue hover:bg-brand-blueDeep text-white"
                       data-testid="gis-generate-btn">
                {pipelineRunning
                  ? <><ArrowClockwise size={14} className="mr-1.5 animate-spin" /> Running…</>
                  : <><Play size={14} className="mr-1.5" weight="fill" /> Generate GIS Data</>}
              </Button>
            </div>
          </div>
        </WizardCard>
      )}

      {/* ── 4. Live pipeline log ───────────────────────────────── */}
      {(pipelineRunning || statusQ.data?.log?.length) ? (
        <WizardCard title="Pipeline progress" testId="gis-pipeline-log">
          <div className="max-h-64 overflow-y-auto space-y-1 text-[11px] font-mono">
            {(statusQ.data?.log || []).slice().reverse().slice(0, 40).reverse().map((e, i) => (
              <div key={`${e.stage}-${i}`}
                    className="flex items-center gap-2 py-0.5">
                {e.status === "complete"
                  ? <CheckCircle size={11} weight="fill" className="text-emerald-500" />
                  : e.status === "processing"
                    ? <ArrowClockwise size={11} className="animate-spin text-brand-blue" />
                    : e.status === "failed"
                      ? <Warning size={11} weight="fill" className="text-rose-500" />
                      : <Circle size={11} className="text-slate-400" />}
                <div className="flex-1">{STAGE_LABEL[e.stage] || e.stage}</div>
                <div className="text-slate-500">{e.status}</div>
              </div>
            ))}
          </div>
        </WizardCard>
      ) : null}

      {/* ── 5. Layer tree ─────────────────────────────────────── */}
      {libraryQ.data?.count > 0 && (
        <WizardCard title={`Project GIS library · ${libraryQ.data.count} layers`}
                     testId="gis-library-tree"
                     right={
                       <div className="flex gap-2">
                         <Button size="sm" variant="outline"
                                   onClick={downloadZip}
                                   data-testid="gis-download-zip">
                           <FileArrowDown size={12} className="mr-1" />
                           Download complete ZIP
                         </Button>
                       </div>
                     }>
          <div className="space-y-2">
            {layerTree.map(([cat, layers]) => (
              <div key={cat} className="border border-slate-200 rounded overflow-hidden">
                <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <FolderSimple size={11} />
                  {cat}
                </div>
                {layers.map((l) => (
                  <div key={l.layerKey}
                        className="flex items-center gap-2 px-3 py-1.5 border-t border-slate-100 hover:bg-slate-50 text-[12px]"
                        data-testid={`gis-layer-${l.layerKey}`}>
                    <MapPin size={11} className="text-slate-500 shrink-0" />
                    <div className="flex-1">
                      <div className="text-slate-800">{l.layerName}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {l.sourceProvider} · {l.resolution} · {l.sourceLicence}
                      </div>
                    </div>
                    {l.feature_count !== undefined && (
                      <div className="text-[10px] text-slate-500 mono">
                        {l.feature_count} features
                      </div>
                    )}
                    <Badge className={`text-[9px] px-1.5 py-0 border ${
                      QUALITY_COLOR[l.qualityRating] || QUALITY_COLOR.Unavailable}`}>
                      {l.qualityRating}
                    </Badge>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </WizardCard>
      )}
    </div>
  );
}
